import cx from 'classnames'
import { type ReactNode } from 'react'
import { RawButton } from '@/components/Button'
import styles from './BillingItemList.module.css'

interface BillingItemListProps {
  children?: ReactNode
}

/**
 * Billing Item List
 */
export function BillingItemList({ children }: BillingItemListProps) {
  return <div className={styles.root}>{children}</div>
}

interface BillingItemProps {
  children?: ReactNode
  /**
   * Controls rendered beside the item rather than inside its button.
   */
  actions?: ReactNode
  onPress?: () => unknown
  isDisabled?: boolean
  isCurrent?: boolean
  testId?: string
}

/**
 * Billing Item
 */
export function BillingItem({
  children,
  actions,
  onPress,
  isDisabled,
  isCurrent,
  testId,
}: BillingItemProps) {
  const ariaCurrent = isCurrent || undefined
  return (
    <div
      data-test={testId}
      className={cx(
        styles.item,
        onPress && !isDisabled && styles.clickable,
        isDisabled && styles.disabled,
      )}
    >
      {onPress ? (
        <RawButton
          className={styles.main}
          isDisabled={isDisabled}
          aria-current={ariaCurrent}
          onPress={onPress}
        >
          {children}
        </RawButton>
      ) : (
        <div className={styles.main} aria-current={ariaCurrent}>
          {children}
        </div>
      )}
      {actions && <div className={styles.actions}>{actions}</div>}
      <div className={styles.shadow} />
    </div>
  )
}

interface BillingItemVisualProps {
  children?: ReactNode
}

/**
 * Billing Item Visual
 */
export function BillingItemVisual({ children }: BillingItemVisualProps) {
  return <div className={styles.visual}>{children}</div>
}

interface BillingItemContentProps {
  children?: ReactNode
}

/**
 * Billing Item Content
 */
export function BillingItemContent({ children }: BillingItemContentProps) {
  return <div className={styles.content}>{children}</div>
}

interface BillingItemTitleProps {
  children?: ReactNode
  testId?: string
}

/**
 * Billing Item Title
 */
export function BillingItemTitle({ children, testId }: BillingItemTitleProps) {
  return (
    <div data-test={testId} className={styles.title}>
      {children}
    </div>
  )
}

interface BillingItemSubtitleProps {
  children?: ReactNode
  testId?: string
}

/**
 * Billing Item Subtitle
 */
export function BillingItemSubtitle({
  children,
  testId,
}: BillingItemSubtitleProps) {
  return (
    <div data-test={testId} className={styles.subtitle}>
      {children}
    </div>
  )
}

interface BillingItemExtrasProps {
  children?: ReactNode
}

/**
 * Billing Item Extras
 */
export function BillingItemExtras({ children }: BillingItemExtrasProps) {
  return <div className={styles.extras}>{children}</div>
}

interface BillingItemBadgeProps {
  children?: ReactNode
}

/**
 * Billing Item Badge
 */
export function BillingItemBadge({ children }: BillingItemBadgeProps) {
  return <div className={styles.badge}>{children}</div>
}
